import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Link, useParams } from 'react-router-dom';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { SeoTrackedKeyword } from '@/types/api/seo';
import { DataTable, PageHeader, FilterTabs, Column } from '@/components/shared';

type CopilotFilter = 'all' | 'copilot_active' | 'copilot_paused' | 'no_copilot';

type Props = {
  keywords: SeoTrackedKeyword[];
  copilotStatusMap: Map<string, string>;
  isLoading: boolean;
  onAddKeywords: () => void;
  onImportFromGSC: () => void;
  onDeleteKeywords: (ids: string[]) => Promise<void>;
  isImporting: boolean;
  importCount: number | null;
  canImport: boolean;
};

const formatUrl = (url: string): string => {
  try {
    const u = new URL(url);
    return u.pathname + (u.search || '');
  } catch {
    return url.replace(/^https?:\/\/[^/]+/, '') || '/';
  }
};

const positionColor = (pos: number | null | undefined): string => {
  if (pos == null) return 'text.secondary';
  if (pos <= 3) return '#0D9D58';
  if (pos <= 10) return '#4285F4';
  if (pos <= 20) return '#F4B400';
  return '#EA4335';
};

const renderChange = (position: number | null | undefined, previous: number | null | undefined) => {
  if (position == null || previous == null) return '—';
  const diff = previous - position;
  if (diff === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (diff > 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2" fontWeight={500}>{diff}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
      <Typography variant="body2" fontWeight={600}>{Math.abs(diff)}</Typography>
    </Box>
  );
};

const SeoKeywordListComponent = function ({
  keywords,
  copilotStatusMap,
  isLoading,
  onAddKeywords,
  onImportFromGSC,
  onDeleteKeywords,
  isImporting,
  importCount,
  canImport,
}: Props): JSX.Element {
  const { projectId = '' } = useParams();
  const basePath = `/seox/projects/${projectId}`;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [copilotFilter, setCopilotFilter] = useState<CopilotFilter>('all');

  const activeCount = keywords.filter((kw) => copilotStatusMap.get(kw.id) === 'active').length;
  const pausedCount = keywords.filter((kw) => copilotStatusMap.get(kw.id) === 'paused').length;
  const noCopilotCount = keywords.filter((kw) => !copilotStatusMap.has(kw.id)).length;

  const filteredKeywords = keywords.filter((kw) => {
    if (copilotFilter === 'all') return true;
    const status = copilotStatusMap.get(kw.id);
    if (copilotFilter === 'copilot_active') return status === 'active';
    if (copilotFilter === 'copilot_paused') return status === 'paused';
    return !status;
  });

  const handleDelete = async () => {
    const ids = Array.from(selected);
    const count = ids.length;
    if (!window.confirm(`Remove ${count} keyword${count !== 1 ? 's' : ''} from tracking?`)) return;
    setIsDeleting(true);
    try {
      await onDeleteKeywords(ids);
      setSelected(new Set());
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<SeoTrackedKeyword>[] = [
    {
      id: 'keyword',
      label: 'Keyword',
      render: (kw) => (
        <Link to={`${basePath}/keywords/${kw.shortId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography variant="body2" fontWeight={500} sx={{ '&:hover': { color: '#054AD9' } }}>
            {kw.keyword}
          </Typography>
        </Link>
      ),
    },
    {
      id: 'position',
      label: 'Position',
      align: 'center',
      render: (kw) => (
        <Chip
          label={kw.latestSnapshot?.position != null ? `#${kw.latestSnapshot.position.toFixed(1)}` : '—'}
          size="small"
          sx={{ fontWeight: 500, fontSize: 12, color: '#fff', backgroundColor: positionColor(kw.latestSnapshot?.position), minWidth: 50, borderRadius: '8px' }}
        />
      ),
    },
    {
      id: 'change',
      label: 'Change',
      align: 'center',
      render: (kw) => renderChange(kw.latestSnapshot?.position, kw.latestSnapshot?.previousPosition),
    },
    {
      id: 'impressions',
      label: 'Impressions',
      align: 'center',
      render: (kw) => (
        <Typography variant="body2">
          {kw.latestSnapshot?.searchVolume != null ? kw.latestSnapshot.searchVolume.toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      id: 'page',
      label: 'Ranking Page',
      render: (kw) => kw.latestSnapshot?.url ? (
        <Typography variant="caption" sx={{ display: 'block', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }} title={kw.latestSnapshot.url}>
          {formatUrl(kw.latestSnapshot.url)}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.disabled">—</Typography>
      ),
    },
    {
      id: 'copilot',
      label: 'Copilot',
      align: 'center',
      render: (kw) => {
        const status = copilotStatusMap.get(kw.id);
        if (!status) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Chip
            icon={<SmartToyIcon sx={{ fontSize: '14px !important' }} />}
            label={status === 'active' ? 'Active' : 'Paused'}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              fontWeight: 500,
              color: status === 'active' ? '#0D9D58' : '#F4B400',
              backgroundColor: status === 'active' ? '#E6F4EA' : '#FEF7E0',
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        );
      },
    },
    {
      id: 'lastChecked',
      label: 'Last Checked',
      render: (kw) => (
        <Typography variant="caption" color="text.secondary">
          {kw.latestSnapshot?.snapshotDate ? new Date(kw.latestSnapshot.snapshotDate).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{}}>
      <PageHeader
        title="Keyword Tracker"
        actions={
          <>
            {canImport && (
              <Button
                variant="outlined"
                startIcon={isImporting ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                onClick={onImportFromGSC}
                disabled={isImporting}
                sx={{ borderColor: '#4285F4', color: '#4285F4', '&:hover': { borderColor: '#3367D6', backgroundColor: '#4285F410' }, textTransform: 'none' }}
              >
                {isImporting ? 'Importing...' : 'Import from GSC'}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddKeywords}
              sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
            >
              Add Keywords
            </Button>
          </>
        }
      />

      {importCount !== null && importCount > 0 && (
        <Alert severity="success" sx={{ mb: 2, fontSize: 13 }} onClose={() => {}}>
          Imported {importCount} keywords from Google Search Console with their current positions and ranking pages.
        </Alert>
      )}
      {importCount === 0 && (
        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }} onClose={() => {}}>
          No keywords found in Google Search Console for this site. Make sure your site has search traffic.
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <FilterTabs
          tabs={[
            { key: 'all', label: 'All', count: keywords.length },
            { key: 'copilot_active', label: 'Copilot Active', count: activeCount },
            { key: 'copilot_paused', label: 'Copilot Paused', count: pausedCount },
            { key: 'no_copilot', label: 'No Copilot', count: noCopilotCount },
          ]}
          activeKey={copilotFilter}
          onChange={(key) => setCopilotFilter(key as CopilotFilter)}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredKeywords}
        isLoading={isLoading}
        rowKey={(kw) => kw.id}
        searchPlaceholder="Search keywords or pages..."
        searchFields={(kw) => `${kw.keyword} ${kw.latestSnapshot?.url ?? ''}`}
        emptyMessage={canImport
          ? 'No keywords tracked yet. Click "Import from GSC" to import keywords, or add them manually.'
          : 'No keywords tracked yet. Add keywords manually to start monitoring rankings.'}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        selectionActions={
          <Tooltip title="Remove selected keywords">
            <IconButton onClick={handleDelete} disabled={isDeleting} sx={{ color: 'error.main' }}>
              {isDeleting ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}
            </IconButton>
          </Tooltip>
        }
      />
    </Box>
  );
};

export default SeoKeywordListComponent;
